import { useState } from "react";
import { Building2, Users, Search, Plus, Filter, Download } from "lucide-react";
import { Button } from "src/vibe-code/rcm-v2/components/ui/button";
import { Input } from "src/vibe-code/rcm-v2/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/vibe-code/rcm-v2/components/ui/select";
import { Badge } from "src/vibe-code/rcm-v2/components/ui/badge";

interface LayoutProps {
  children: React.ReactNode;
  activeTab: "dealers" | "employees";
  onTabChange: (tab: "dealers" | "employees") => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAddClick: () => void;
  onExportClick: () => void;
  dealerCount: number;
  employeeCount: number;
}

export function Layout({
  children,
  activeTab,
  onTabChange,
  searchQuery,
  onSearchChange,
  onAddClick,
  onExportClick,
  dealerCount,
  employeeCount,
}: LayoutProps) {
  const [showFilters, setShowFilters] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b gradient-subtle border-border shadow-custom-sm">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="flex justify-center items-center w-8 h-8 rounded-lg gradient-primary">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-xl font-semibold">Dealer Management</h1>
              </div>

              {/* Search Bar */}
              <div className="flex items-center ml-8 space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 w-4 h-4 transform -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={`Search ${activeTab}...`}
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="pl-10 w-80"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2"
              >
                <Filter className="w-4 h-4" />
                <span>Filter</span>
              </Button>
              <Button
                variant="outline"
                onClick={onExportClick}
                className="flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </Button>
              <Button
                onClick={onAddClick}
                className="flex items-center space-x-2 text-white gradient-primary"
              >
                <Plus className="w-4 h-4" />
                <span>
                  Add {activeTab === "dealers" ? "Dealer" : "Employee"}
                </span>
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          {showFilters && (
            <div className="p-4 mt-4 rounded-lg border bg-muted/50">
              <div className="flex items-center space-x-4">
                {activeTab === "dealers" && (
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium">Type:</label>
                    <Select>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Independent Dealership">
                          Independent Dealership
                        </SelectItem>
                        <SelectItem value="Mobile Home Militia">
                          Mobile Home Militia
                        </SelectItem>
                        <SelectItem value="Palm Harbor Enterprise">
                          Palm Harbor Enterprise
                        </SelectItem>
                        <SelectItem value="Cavco Manufactured Enterprise">
                          Cavco Manufactured Enterprise
                        </SelectItem>
                        <SelectItem value="Solitaire Homes Enterprise">
                          Solitaire Homes Enterprise
                        </SelectItem>
                        <SelectItem value="Titan Enterprise">
                          Titan Enterprise
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {activeTab === "employees" && (
                  <>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium">Role:</label>
                      <Select>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="All roles" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="director">Director</SelectItem>
                          <SelectItem value="vp">VP</SelectItem>
                          <SelectItem value="ceo">CEO</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium">
                        Employee Status:
                      </label>
                      <Select>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Active & Added" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active-added">
                            Active & Added by Dealer
                          </SelectItem>
                          <SelectItem value="active">Active Only</SelectItem>
                          <SelectItem value="added">
                            Added by Dealer Only
                          </SelectItem>
                          <SelectItem value="inactive">
                            No Longer with Company
                          </SelectItem>
                          <SelectItem value="deleted">
                            Deleted by Dealer
                          </SelectItem>
                          <SelectItem value="all">All Statuses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <Button variant="outline" size="sm">
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-80px)]">
        {/* Sidebar */}
        <aside className="w-64 border-r bg-sidebar-bg border-border">
          <nav className="p-4">
            <div className="space-y-2">
              <button
                onClick={() => onTabChange("dealers")}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === "dealers"
                    ? "bg-sidebar-active text-white"
                    : "text-sidebar-foreground hover:bg-sidebar-hover"
                }`}
              >
                <Building2 className="w-5 h-5" />
                <span className="font-medium">Dealers</span>
                <Badge variant="secondary" className="ml-auto">
                  {dealerCount}
                </Badge>
              </button>

              <button
                onClick={() => onTabChange("employees")}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === "employees"
                    ? "bg-sidebar-active text-white"
                    : "text-sidebar-foreground hover:bg-sidebar-hover"
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Employees</span>
                <Badge variant="secondary" className="ml-auto">
                  {employeeCount}
                </Badge>
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="overflow-auto flex-1">{children}</main>
      </div>
    </div>
  );
}
